"use client";
import { useFormContext, useWatch } from "react-hook-form";
import { useHandleApiCall } from "../pages/StepPage/components/apiCalls/useHandleApiCall";
import { CrossCard, CrossCardOption } from "./CrossCard";
import { CrossProduct, CrossProductType, CrossType } from "./types";
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiCallsTypes } from "../pages/StepPage/components/apiCalls/contants";
import { useContextStore } from "@/src/stores/useContextStore";
import { safeParseJson } from "@/src/shared/utils/form";
import { useDebouncedCallback } from "use-debounce";
import { isEqual } from "lodash";
import { create } from "zustand";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import { useSearchParams } from "next/navigation";
import { BreakPointsPx, PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { updateProcessInstanceField } from "@/src/api/services/processInstance";
import { BaseHintProps } from "../HintCard/lib/type";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import { useMediaQuery } from "@mui/material";

const formatNumberWithSpaces = (number: number): string => {
  return new Intl.NumberFormat("uk-UA").format(number);
};

export const useSelectedCrossOptions = () => {
  const { setValue, getValues, control } = useFormContext();
  const pickedCrosses = useWatch({ control, name: "pickedCrosses" });
  const crosses = useCrossesOptions({});
  // const set = (crossOptions: CrossCardOption[]) => {
  //     setValue(
  //         "pickedCrosses",
  //         crossOptions.map(({ id }) => id),
  //     );
  // };
  const get = (crossId: string) => {
    const selectedCross = crosses.find(({ options }) => {
      return options.find(({ id }) => id === crossId);
    });
    if (!selectedCross) return null;

    const selectedCrossOption = selectedCross?.options.find(
      ({ id }) => id === crossId,
    )!;

    const { price, insuranceAmount, id } = selectedCrossOption;

    const res = {
      id: selectedCross.id,
      type: selectedCross.type,
      option: {
        value: String(price),
        label: `${formatNumberWithSpaces(insuranceAmount)} грн — ${price} грн`,
        id,
      },
    };

    return res;
  };

  const selectedCrossOptions = useMemo(
    () => (pickedCrosses || []).map((id: string) => get(id)),
    [pickedCrosses, crosses],
  );

  useEffect(() => {
    const currentOptions = getValues("selectedCards");
    const newOptions = selectedCrossOptions.filter((option: any) => option);
    if (isEqual(currentOptions, newOptions)) {
      return;
    }

    setValue("selectedCards", newOptions);
  }, [selectedCrossOptions]);

  // return { get, set };
};

const getCrossOptions = (type: CrossType) => {
  const { variables = {}, getVariable } = useContextStore.getState();
  const regex = new RegExp(`^${type}Premium_(\\d+)$`);

  const variablesKeys = Object.keys(variables);

  const crossOptionsVariables = variablesKeys
    .map((variableKey) => {
      const match = variableKey.match(regex);
      if (match) {
        return { variableKey, insuranceAmount: parseInt(match[1], 10) };
      }
      return null;
    })
    .filter((v) => !!v);

  const options = crossOptionsVariables.map(
    ({ variableKey, insuranceAmount }: any) => ({
      price: safeParseJson(getVariable(variableKey)?.value || "")?.value || 0,
      insuranceAmount,
      id: variableKey,
    }),
  );

  return options;
};

type CrossDetails = {
  name: string;
  description: string;
  hint: {
    isShown?: boolean;
    hintLabel?: string;
    hintsList?: { contentDescription?: string }[];
  };
};
const useCrossesOptions = ({
  legkyiZahystDetails,
  DMSDetails,
}: {
  legkyiZahystDetails?: CrossDetails;
  DMSDetails?: CrossDetails;
}) => {
  const { control } = useFormContext();
  const [crossOptions, setCrossOptions] = useState<CrossProduct[]>([]);

  const formValues = useWatch({ control });

  const options = useMemo(() => {
    const regex = new RegExp(`^${CrossType.LegkyiZahyst}Premium_(\\d+)$`);
    const regex2 = new RegExp(`^${CrossType.DMSDTP}Premium_(\\d+)$`);

    return JSON.stringify(
      Object.entries(formValues).filter(
        ([key]) => key.match(regex) || key.match(regex2),
      ),
    );
  }, [formValues]);

  const legkiyZahistHints: any =
    legkyiZahystDetails?.hint?.hintsList?.map(({ contentDescription }) => ({
      contentDescription,
      contentTitle: legkyiZahystDetails?.hint?.hintLabel || "",
      isShown: legkyiZahystDetails?.hint?.isShown,
    })) || [];

  const DMSHints: any =
    DMSDetails?.hint?.hintsList?.map(({ contentDescription }) => ({
      contentDescription,
      contentTitle: DMSDetails?.hint?.hintLabel || "",
      isShown: DMSDetails?.hint?.isShown,
    })) || [];

  useEffect(() => {
    setCrossOptions([
      {
        id: CrossType.LegkyiZahyst,
        type: CrossProductType.CrossLegkyiZahystTariffCalculator,
        description:
          legkyiZahystDetails?.description ||
          "КОМПЛЕКСНЕ СТРАХУВАННЯ ВІДПОВІДАЛЬНОСТІ, ЯКА ВИНИКАЄ ВНАСЛІДОК ВИКОРИСТАННЯ (ЕКСПЛУАТАЦІЇ) НАЗЕМНИХ ТРАНСПОРТНИХ ЗАСОБІВ І СТРАХУВАННЯ ВОДІЇВ ТА ПАСАЖИРІВ ВІД НЕЩАСНИХ ВИПАДКІВ НА ТРАНСПОРТІ",
        name: legkyiZahystDetails?.name ?? "Додаткове покриття (ДЦВ+НВ)",
        hints: legkiyZahistHints,
        options: getCrossOptions(CrossType.LegkyiZahyst),
      },
      {
        id: CrossType.DMSDTP,
        type: CrossProductType.CrossDMSDTPTariffCalculator,
        description:
          DMSDetails?.description ||
          "КОМПЛЕКСНЕ СТРАХУВАННЯ ВІДПОВІДАЛЬНОСТІ, ЯКА ВИНИКАЄ ВНАСЛІДОК ВИКОРИСТАННЯ (ЕКСПЛУАТАЦІЇ) НАЗЕМНИХ ТРАНСПОРТНИХ ЗАСОБІВ І СТРАХУВАННЯ ВОДІЇВ ТА ПАСАЖИРІВ ВІД НЕЩАСНИХ ВИПАДКІВ НА ТРАНСПОРТІ",
        name: DMSDetails?.name || "Медичне страхування при ДТП",
        hints: DMSHints,
        options: getCrossOptions(CrossType.DMSDTP),
      },
    ]);
  }, [options]);

  return crossOptions;
};

export const useCrossHint = create<{
  hintedCrossId: string;
  selectHintedCross: (crossId: string) => void;
}>((set, get) => ({
  hintedCrossId: "",
  selectHintedCross: (crossId) => {
    const currentHintedCrossId = get().hintedCrossId;
    set({ hintedCrossId: currentHintedCrossId === crossId ? "" : crossId });
  },
}));

const useHandleCrossSelect = () => {
  const { setValue, getValues } = useFormContext();

  const getContextVariable = useContextStore((state) => state.getVariable);

  const updateField = useUpdateField();

  const handleMultipleCrossSelect = useDebouncedCallback(
    async (
      pickedCrosses: string[],
      price: number = 0,
      crossPrices: Record<string, number> = {},
    ) => {
      // handleApiCallByName(ApiCallsTypes.PICK_CROSSES_ACTION, true, {
      //   formDataOverride: { pickedCrosses },
      // });
      const currentPickedCrosses = getValues("pickedCrosses");
      if (isEqual(currentPickedCrosses, pickedCrosses)) return;
      const dmsRegex = new RegExp(`^${CrossType.DMSDTP}Premium_(\\d+)$`);

      const pickedDMSCrosses = pickedCrosses.filter((cross: string) =>
        cross.match(dmsRegex),
      );
      const pickedDMSTarifs = pickedDMSCrosses.map((cross) =>
        cross.replace("Premium", "Tariff"),
      );
      setValue("pickedCrosses", pickedCrosses);
      await updateField("pickedCrosses", pickedCrosses);

      const isCrossesPicked = !!pickedCrosses.length;
      const currentPremium = getValues("premium");
      const priceWithCrosses = Number(currentPremium) + price;

      setValue("isCrossesPicked", isCrossesPicked);
      setValue("priceWithCrosses", priceWithCrosses);
      setValue("DMSDTPPremium_picked_premium", crossPrices["DMSDTP"] || 0);
      setValue(
        "LegkyiZahystPremium_picked_premium",
        crossPrices["LegkyiZahyst"] || 0,
      );
      await updateField("isCrossesPicked", isCrossesPicked);
      await updateField("priceWithCrosses", priceWithCrosses);

      if (pickedDMSTarifs.length) {
        const pickedTarif = pickedDMSTarifs[0];
        const pickedTarifValue = getContextVariable(pickedTarif)?.value || 0;
        setValue("DMSDTPTariff_picked", pickedTarifValue);
        await updateField("DMSDTPTariff_picked", pickedTarifValue);
      } else {
        setValue("DMSDTPTariff_picked", null);
        await updateField("DMSDTPTariff_picked", "null");
      }
    },
    0,
  );

  return { handleMultipleCrossSelect };
};

const getMultipleCrossSelect = (selectedCards: { id: string, option: CrossCardOption }[] | undefined): {
  pickedCrosses: string[],
  price: number,
  crossPrices: Record<string, number>
} => {
  return {
    pickedCrosses: selectedCards?.reduce(
      (sum, { option }) => (option?.id ? [...sum, option.id] : sum),
      [] as string[],
    ) || [],
    price: selectedCards?.reduce(
      (sum, { option }) =>
        Number(option?.value) ? sum + Number(option.value) : sum,
      0,
    ) || 0,
    crossPrices: selectedCards?.reduce(
      (sum, { id, option }) =>
        option?.id ? { ...sum, [id]: Number(option.value) } : sum,
      {} as Record<string, number>,
    ) || {},
  }
}

export const CrossProductCard = ({
  legkyiZahystDetails,
  DMSDetails,
}: {
  products: CrossProduct[];
  columns?: number;
  params?: Record<CrossProductType, any>;

  legkyiZahystDetails?: CrossDetails;
  DMSDetails?: CrossDetails;
}) => {
  const { control, getValues } = useFormContext();
  const isMobile = useMediaQuery(`(max-width:${BreakPointsPx.md})`);
  useSelectedCrossOptions();
  // const { handleApiCallByName } = useHandleApiCall();

  const rawProducts = useCrossesOptions({ legkyiZahystDetails, DMSDetails });
  const products =
    getValues("vehicleType")?.value?.includes("B") &&
    getValues("datePeriod")?.value?.includes("12Months")
      ? rawProducts
      : [];

  const { handleMultipleCrossSelect } = useHandleCrossSelect();
  const selectedCards = useWatch({
    name: "selectedCards",
    control,
  }) as { id: string; option: CrossCardOption }[] | undefined;
  const selectedCardsRef = useRef<any>(selectedCards || null);

  useEffect(() => {
    if (selectedCards === undefined) return;
    // if (!selectedCardsRef.current) {
    //   selectedCardsRef.current = selectedCards;
    //   return;
    // }

    if (isEqual(selectedCardsRef.current, selectedCards)) {
      return;
    }

    const filteredSelectedCards = selectedCards?.filter(({ id }) => {
      return products.find((product) => product.id === id);
    });

    selectedCardsRef.current = filteredSelectedCards;
    console.log("selectedCards", filteredSelectedCards);
    const { pickedCrosses, price, crossPrices } = getMultipleCrossSelect(filteredSelectedCards);
    handleMultipleCrossSelect(pickedCrosses, price, crossPrices);
  }, [products, selectedCards]);

  return (
    <div
      className="grid min-h-32 gap-4"
      style={{
        gridTemplateColumns: isMobile
          ? `repeat(1, minmax(0, 1fr))`
          : `repeat(${products.length}, minmax(0, 1fr))`,
      }}
    >
      {products.map((product) => {
          const minimalPrice = product.options.reduce(
              (min, { price }) => (Math.min(price, min)),
              Infinity
          ) || 0;

          return (
          <CrossCard
            key={product.id}
            title={product.name}
            description={product.description}
            details={`Від ${minimalPrice} грн`}
            options={product.options.map(
              ({ insuranceAmount, price, id }) => ({
                value: String(price),
                label: `${formatNumberWithSpaces(insuranceAmount)} грн — ${price} грн`,
                id,
              }),
            )}
            id={product.id}
            type={product.type}
            hints={product.hints}
            multiselect
          />
        );
      })}
    </div>
  );
};
